import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import {
  listingsAPI,
  orgListingsAPI,
  transactionsAPI,
  analyticsAPI,
  assignmentsAPI,
} from '../../api';
import { orgAudienceAPI } from '../../api/clients/orgAudienceAPI';
import { computePerformanceMetrics } from '../../util/performanceMetrics';
import config from '../../config';
import { PAGE_TYPE_QUERY } from '../../util/listingType';

// ================ Action types ================ //

export const SET_ORG_ID = 'app/OrganizationHomePage/SET_ORG_ID';
export const SET_TASK_COMPLETED = 'app/OrganizationHomePage/SET_TASK_COMPLETED';
export const SET_WIZARD_STATE = 'app/OrganizationHomePage/SET_WIZARD_STATE';
export const FETCH_SHOW_LISTINGS_REQUEST = 'app/OrganizationHomePage/FETCH_SHOW_LISTINGS_REQUEST';
export const FETCH_SHOW_LISTINGS_SUCCESS = 'app/OrganizationHomePage/FETCH_SHOW_LISTINGS_SUCCESS';
export const FETCH_SHOW_LISTINGS_ERROR = 'app/OrganizationHomePage/FETCH_SHOW_LISTINGS_ERROR';
export const FETCH_NEWSLETTER_RATES_SUCCESS = 'app/OrganizationHomePage/FETCH_NEWSLETTER_RATES_SUCCESS';
// Ended assignments — powers the dismissible "Gig Boost ended" notice.
export const FETCH_ENDED_ASSIGNMENTS_SUCCESS =
  'app/OrganizationHomePage/FETCH_ENDED_ASSIGNMENTS_SUCCESS';

export const FETCH_ORG_PERFORMANCE_REQUEST = 'app/OrganizationHomePage/FETCH_ORG_PERFORMANCE_REQUEST';
export const FETCH_ORG_PERFORMANCE_SUCCESS = 'app/OrganizationHomePage/FETCH_ORG_PERFORMANCE_SUCCESS';
export const FETCH_ORG_PERFORMANCE_ERROR = 'app/OrganizationHomePage/FETCH_ORG_PERFORMANCE_ERROR';

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  endedAssignments: [],
  tasks: {
    inviteTeamTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    setupSubscriptionTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    launchResearchTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    importFansTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
    listShowsTask: {
      completed: false,
      inProgress: false,
      error: null,
    },
  },
  wizardState: {
    dataLoaded: false,
    shouldShow: false,
    shouldAutoShow: false,
    startingStep: 1,
    hasIncompleteSteps: false,
  },
  // Show listings for the organization
  showListingIds: [],
  showListingsInProgress: false,
  showListingsLoaded: false,
  showListingsError: null,
  // Newsletter open rates from email campaigns
  newsletterRates: [],
  // Performance overview (members / pledged revenue / page views)
  orgPerformanceMetrics: null,
  orgPerformanceLoading: false,
  orgPerformanceError: null,
};

export default function organizationHomePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_ORG_ID:
      // Switching orgs (same mounted component, different :orgId in the URL)
      // must drop the previous org's data so it can't flash under the new
      // org while its fetch is in flight. Same orgId → keep state so a
      // revisit/refetch stays stale-while-revalidate.
      return payload === state.orgId ? state : { ...initialState, orgId: payload };
    case SET_TASK_COMPLETED:
      return {
        ...state,
        tasks: {
          ...state.tasks,
          [payload.taskName]: {
            ...state.tasks[payload.taskName],
            completed: payload.completed,
            inProgress: false,
            error: null,
          },
        },
      };

    case SET_WIZARD_STATE:
      return {
        ...state,
        wizardState: {
          ...state.wizardState,
          ...payload,
        },
      };

    case FETCH_SHOW_LISTINGS_REQUEST:
      return {
        ...state,
        showListingsInProgress: true,
        showListingsError: null,
      };

    case FETCH_SHOW_LISTINGS_SUCCESS:
      return {
        ...state,
        showListingIds: payload.listingIds,
        showListingsInProgress: false,
        showListingsLoaded: true,
      };

    case FETCH_SHOW_LISTINGS_ERROR:
      return {
        ...state,
        showListingsInProgress: false,
        showListingsError: payload,
      };

    case FETCH_NEWSLETTER_RATES_SUCCESS:
      return {
        ...state,
        newsletterRates: payload,
      };

    case FETCH_ENDED_ASSIGNMENTS_SUCCESS:
      return { ...state, endedAssignments: payload };

    case FETCH_ORG_PERFORMANCE_REQUEST:
      return { ...state, orgPerformanceLoading: true, orgPerformanceError: null };

    case FETCH_ORG_PERFORMANCE_SUCCESS:
      return { ...state, orgPerformanceLoading: false, orgPerformanceMetrics: payload };

    case FETCH_ORG_PERFORMANCE_ERROR:
      return { ...state, orgPerformanceLoading: false, orgPerformanceError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setOrgId = orgId => ({
  type: SET_ORG_ID,
  payload: orgId,
});

export const setTaskCompleted = (taskName, isCompleted) => ({
  type: SET_TASK_COMPLETED,
  payload: {
    taskName,
    completed: isCompleted,
  },
});

export const setWizardState = wizardState => ({
  type: SET_WIZARD_STATE,
  payload: wizardState,
});

// Legacy action creators for compatibility
export const setTeamInviteCompleted = isCompleted => setTaskCompleted('inviteTeamTask', isCompleted);

// Show listings action creators
export const fetchShowListingsRequest = () => ({
  type: FETCH_SHOW_LISTINGS_REQUEST,
});

export const fetchShowListingsSuccess = listingIds => ({
  type: FETCH_SHOW_LISTINGS_SUCCESS,
  payload: { listingIds },
});

export const fetchShowListingsError = error => ({
  type: FETCH_SHOW_LISTINGS_ERROR,
  payload: error,
});

// ================ Selectors ================ //

export const selectWizardState = state => state.OrganizationHomePage.wizardState;
export const selectTasks = state => state.OrganizationHomePage.tasks;
export const selectShowListingIds = state => state.OrganizationHomePage.showListingIds;
export const selectNewsletterRates = state => state.OrganizationHomePage.newsletterRates;
export const selectOrgPerformanceMetrics = state =>
  state.OrganizationHomePage.orgPerformanceMetrics;

// ================ Thunks ================ //

// Fetch show listings for the organization. Routed through the server's
// Integration SDK proxy so non-owner team members also see the org's
// listings (they live on the owner's account; Marketplace SDK can't see
// across users).
export const fetchShowListings = orgId => async dispatch => {
  dispatch(fetchShowListingsRequest());

  try {
    const response = await orgListingsAPI.query(orgId, {
      pub_type: PAGE_TYPE_QUERY,
      states: 'draft,pendingApproval,published,closed',
      include: 'images,author',
      'fields.image': 'variants.square-small,variants.square-small2x',
      'limit.images': 1,
    });

    const listings = response?.data || [];
    const listingIds = listings.map(l => l.id);

    dispatch(addMarketplaceEntities({ data: response }));
    dispatch(fetchShowListingsSuccess(listingIds));

    return listings;
  } catch (e) {
    dispatch(fetchShowListingsError(storableError(e)));
    console.error('Error fetching show listings:', e);
    return [];
  }
};

// Fetch real newsletter open rates from email campaigns
export const fetchNewsletterRates = (orgId) => async (dispatch) => {
  try {
    // Org-scoped — was fetching the viewer's own campaigns (audienceAPI.getEmails
    // with no orgId), so a non-owner saw wrong/empty newsletter data.
    const response = await orgAudienceAPI.getEmails(orgId);
    const emails = response.data?.data?.emails || [];

    // Get sent emails with open rate data, sorted by most recent. Subject
    // falls back to campaign name (subject is optional).
    const rates = emails
      .filter(e => e.status === 'sent')
      .sort((a, b) => new Date(b.sentAt || b.createdAt || 0) - new Date(a.sentAt || a.createdAt || 0))
      .slice(0, 5)
      .map(e => ({
        title: e.subject || e.name,
        rate: e.openRate || e.openedCount || 0,
      }));

    dispatch({ type: FETCH_NEWSLETTER_RATES_SUCCESS, payload: rates });
    return rates;
  } catch (e) {
    console.error('Error fetching newsletter rates:', e);
    dispatch({ type: FETCH_NEWSLETTER_RATES_SUCCESS, payload: [] });
    return [];
  }
};

// ---- Performance overview (members / pledged revenue / page views) ----

const FETCH_ORG_PERF_DEFAULT_RANGE = 190;

// Org-scoped raw fetchers. Transactions go through the server proxy
// (Integration SDK) so non-owner members see the org's sales; audience
// lists come from the org-audience proxy; page views from GA4.
const fetchOrgPerfTransactions = async orgId => {
  try {
    const res = await transactionsAPI.query({
      meta_organizationId: orgId,
      include: ['listing'],
    });
    return denormalisedResponseEntities(res.data);
  } catch (e) {
    console.warn('[OrgHome] transactions fetch failed:', e?.message || e);
    return [];
  }
};

const fetchOrgPerfAudienceLists = async orgId => {
  try {
    const res = await orgAudienceAPI.getLists(orgId);
    return res.data?.data?.lists || [];
  } catch (e) {
    console.warn('[OrgHome] audience lists fetch failed:', e?.message || e);
    return [];
  }
};

const fetchOrgPerfPageViews = async rangeDays => {
  try {
    const res = await analyticsAPI.getPageViews({
      startDate: `${rangeDays}daysAgo`,
      endDate: 'today',
    });
    return res.data?.data || { totalViews: 0, previousTotalViews: 0, viewsByDate: {} };
  } catch (e) {
    console.warn('[OrgHome] page views fetch failed:', e?.message || e);
    return { totalViews: 0, previousTotalViews: 0, viewsByDate: {} };
  }
};

/**
 * Fetch the org performance overview: Total Members (audience), Pledged
 * Revenue (org transactions) and Page Views (GA4), shaped for the shared
 * PerformanceOverviewCard. Raw inputs are cached for date-range changes.
 */
export const fetchOrgPerformanceMetrics = (orgId, rangeDays = FETCH_ORG_PERF_DEFAULT_RANGE) => async dispatch => {
  if (!orgId) return null;
  dispatch({ type: FETCH_ORG_PERFORMANCE_REQUEST });

  try {
    const [transactions, audienceLists, pageViewData] = await Promise.all([
      fetchOrgPerfTransactions(orgId),
      fetchOrgPerfAudienceLists(orgId),
      fetchOrgPerfPageViews(rangeDays),
    ]);

    const metrics = {
      ...computePerformanceMetrics({ transactions, audienceLists, pageViewData, rangeDays }),
      _rawTransactions: transactions,
      _rawAudienceLists: audienceLists,
      _rawPageViewData: pageViewData,
      _rangeDays: rangeDays,
    };

    dispatch({ type: FETCH_ORG_PERFORMANCE_SUCCESS, payload: metrics });
    return metrics;
  } catch (e) {
    console.error('Error fetching org performance metrics:', e);
    dispatch({ type: FETCH_ORG_PERFORMANCE_ERROR, payload: storableError(e) });
    return null;
  }
};

/**
 * Re-process the org performance overview when the date range changes.
 * Revenue + members re-slice from cache instantly; page views are a GA4
 * per-window aggregate so they're re-fetched for the new range. A race
 * guard prevents a stale response overwriting a newer selection.
 */
export const changeOrgPerformanceDateRange = (orgId, rangeDays) => async (dispatch, getState) => {
  const prev = getState().OrganizationHomePage.orgPerformanceMetrics;
  if (!prev || !prev._rawTransactions) return;

  const reprocess = pageViewData => {
    const cur = getState().OrganizationHomePage.orgPerformanceMetrics || prev;
    const transactions = cur._rawTransactions || [];
    const audienceLists = cur._rawAudienceLists || [];
    dispatch({
      type: FETCH_ORG_PERFORMANCE_SUCCESS,
      payload: {
        ...computePerformanceMetrics({ transactions, audienceLists, pageViewData, rangeDays }),
        _rawTransactions: transactions,
        _rawAudienceLists: audienceLists,
        _rawPageViewData: pageViewData,
        _rangeDays: rangeDays,
      },
    });
  };

  // Instant pass from cache, then correct Views with a fresh GA fetch.
  reprocess(prev._rawPageViewData);
  const pageViewData = await fetchOrgPerfPageViews(rangeDays);
  if (getState().OrganizationHomePage.orgPerformanceMetrics?._rangeDays === rangeDays) {
    reprocess(pageViewData);
  }
};

// Discard a draft listing. Still uses Marketplace SDK because the
// Integration SDK has no equivalent op — this works for the owner only.
// Non-owners (admin+) can't discard via this path; per the permission
// matrix DELETE_SHOWS is owner+admin, so we'd need a different mechanism
// for admins. For now the duck takes orgId so the re-fetch is scoped.
export const discardDraft = (listingId, orgId) => async (dispatch, getState, sdk) => {
  await sdk.ownListings.discardDraft({ id: listingId });
  dispatch(fetchShowListings(orgId));
};

// Close a published listing. listingsAPI.close goes through the server
// (which can use Integration SDK for non-owners) — kept as-is.
export const closeListing = (listingId, orgId) => async dispatch => {
  await listingsAPI.close({ id: listingId });
  dispatch(fetchShowListings(orgId));
};

// Helper to check if organization profile has been completed
const isOrgProfileCompleted = (organizationProfiles, selectedOrgId) => {
  if (!organizationProfiles || organizationProfiles.length === 0) {
    return false;
  }
  const selectedOrg = organizationProfiles.find(p => p.id === selectedOrgId);
  if (!selectedOrg) return false;

  const hasBio = selectedOrg.bio && selectedOrg.bio.trim() !== '';
  const hasGenres = !!selectedOrg.genres;
  const hasFounded = !!selectedOrg.founded;
  const hasLocation = !!selectedOrg.location;
  const hasWebsite = selectedOrg.website && selectedOrg.website.trim() !== '';
  const hasSocialLinks = !!(
    (selectedOrg.facebookLink && selectedOrg.facebookLink.trim() !== '') ||
    (selectedOrg.instagramLink && selectedOrg.instagramLink.trim() !== '') ||
    (selectedOrg.linkedinLink && selectedOrg.linkedinLink.trim() !== '')
  );

  return hasBio || hasGenres || hasFounded || hasLocation || hasWebsite || hasSocialLinks;
};

// Check localStorage to see if wizard was already auto-shown for this org
// Was the org-onboarding wizard already completed for this orgId?
// Server truth (privateData.orgOnboardingCompleted[orgId]) takes
// precedence over localStorage so the flag survives logout, fresh
// browsers, and new devices — fixing Yuko's "pop-up re-appears every
// time" report which was caused by localStorage alone. Per-orgId
// because a user can manage multiple orgs and each onboards once.
const wasWizardShownForOrg = (orgId, currentUser) => {
  const map =
    currentUser?.attributes?.profile?.privateData?.orgOnboardingCompleted;
  if (map && map[orgId] === true) return true;
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(`wizard_auto_shown_${orgId}`) === 'true';
  } catch (e) {
    return false;
  }
};

// Mark the org-onboarding wizard as completed for this orgId. Writes
// localStorage (instant same-session quiet) AND merges into the
// privateData.orgOnboardingCompleted map (durable across devices).
// Thunk shape so the page can dispatch from any handler.
export const markWizardAsShownForOrg = orgId => async (dispatch, getState, sdk) => {
  if (!orgId) return;

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(`wizard_auto_shown_${orgId}`, 'true');
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  try {
    const state = getState();
    const existing =
      state.user?.currentUser?.attributes?.profile?.privateData
        ?.orgOnboardingCompleted || {};
    await sdk.currentUser.updateProfile(
      {
        privateData: {
          orgOnboardingCompleted: { ...existing, [orgId]: true },
        },
      },
      { expand: true }
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to persist orgOnboardingCompleted to privateData:', e);
  }
};

// Fetch organization profile completion status
export const fetchOrganizationProfileCompleted = () => async (dispatch, getState) => {
  const state = getState();
  const { organizationProfiles } = state.organizations;
  const currentUser = state.user?.currentUser;
  const selectedOrgId = currentUser?.attributes?.profile?.publicData?.selectedOrganizationProfileId;

  const isCompleted = isOrgProfileCompleted(organizationProfiles, selectedOrgId);
  // For now, we don't have a specific task for org profile in the new design
  // The wizard handles org profile setup
};

// Fetch listing (show) completion status for the org. Scoped to the org
// via the server proxy so non-owner members see the same completion bit
// as the owner.
export const fetchListingCompleted = orgId => async dispatch => {
  try {
    const response = await orgListingsAPI.query(orgId, {
      pub_type: PAGE_TYPE_QUERY,
      states: 'published',
      'limit.images': 0,
    });
    const listings = response?.data || [];
    const isCompleted = listings.length > 0;

    dispatch(setTaskCompleted('listShowsTask', isCompleted));
  } catch (e) {
    console.error('Error fetching listings:', e);
  }
};

/**
 * Assignments that have ended, for the dashboard notice. Best-effort: the
 * home page must still render if this call fails, so the error is swallowed
 * and the notice simply doesn't appear.
 */
export const fetchEndedAssignments = orgId => async dispatch => {
  if (!orgId) return;
  try {
    const res = await assignmentsAPI.listForOrg(orgId);
    const body = res?.data ?? res ?? {};
    const payload = body?.data ?? body;
    const list = payload?.assignments || [];
    // The endpoint returns creator display names in a separate map; fold the
    // name onto the record so the notice can name who the Gig Boost was with.
    const creators = payload?.creators || {};
    dispatch({
      type: FETCH_ENDED_ASSIGNMENTS_SUCCESS,
      payload: list
        .filter(a => ['revoked', 'expired'].includes(a?.status))
        .map(a => ({ ...a, creatorName: creators[a?.creatorId]?.displayName || null })),
    });
  } catch (e) {
    // Non-fatal — no notice is better than a broken dashboard.
  }
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  // Get orgId from URL params
  const { orgId } = params;

  // Reset the slice when the org actually changes so the previous org's
  // data can't flash under the new org during its fetch.
  dispatch(setOrgId(orgId));

  // Fetch organization profiles first
  const organizationProfiles = await dispatch(fetchOrganizationProfiles());

  // Find the selected organization from URL param
  const selectedOrganization = organizationProfiles?.find(p => p.id === orgId) || null;

  // Fetch listing completion status, show listings, newsletter data, and
  // the performance overview in parallel.
  await Promise.all([
    dispatch(fetchListingCompleted(orgId)),
    dispatch(fetchShowListings(orgId)),
    dispatch(fetchNewsletterRates(orgId)),
    dispatch(fetchOrgPerformanceMetrics(orgId)),
    dispatch(fetchEndedAssignments(orgId)),
  ]);

  // Get updated state
  const state = getState();
  const { tasks } = state.OrganizationHomePage;

  // Calculate wizard state
  const orgProfileCompleted = isOrgProfileCompleted(organizationProfiles, orgId);
  const teamInviteCompleted = tasks.inviteTeamTask.completed;
  const showPageCompleted = tasks.listShowsTask.completed;

  // Calculate starting step
  let startingStep = null;
  if (!orgProfileCompleted) startingStep = 1;
  else if (!teamInviteCompleted) startingStep = 2;
  else if (!showPageCompleted) startingStep = 3;

  const shouldShow = startingStep !== null;
  const currentUser = state.user?.currentUser;
  const wasAlreadyShown = wasWizardShownForOrg(orgId, currentUser);
  const shouldAutoShow = shouldShow && !wasAlreadyShown;

  dispatch(setWizardState({
    dataLoaded: true,
    shouldShow,
    shouldAutoShow,
    startingStep: startingStep || 1,
    hasIncompleteSteps: shouldShow,
  }));

  return { selectedOrganization };
};

// Thunk to recalculate wizard state after a step is completed
export const recalculateWizardState = () => (dispatch, getState) => {
  const state = getState();
  const { organizationProfiles } = state.organizations;
  const { tasks } = state.OrganizationHomePage;
  const currentUser = state.user?.currentUser;

  const selectedOrgId = currentUser?.attributes?.profile?.publicData?.selectedOrganizationProfileId;

  const orgProfileCompleted = isOrgProfileCompleted(organizationProfiles, selectedOrgId);
  const teamInviteCompleted = tasks.inviteTeamTask.completed;
  const showPageCompleted = tasks.listShowsTask.completed;

  let startingStep = null;
  if (!orgProfileCompleted) startingStep = 1;
  else if (!teamInviteCompleted) startingStep = 2;
  else if (!showPageCompleted) startingStep = 3;

  const shouldShow = startingStep !== null;

  dispatch(setWizardState({
    dataLoaded: true,
    shouldShow,
    shouldAutoShow: false,
    startingStep: startingStep || 1,
    hasIncompleteSteps: shouldShow,
  }));
};
